//eslint-disable-next-line react-hooks/exhaustive-deps
import React, { useEffect, useState, useCallback } from "react";
import { Switch, Route, useHistory } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import { useDispatch, useSelector } from "react-redux";
import SignInPage from "pages/Auth/SignIn";
import SignUpPage from "pages/Auth/SignUp";
import ConfirmReviewer from "pages/ConfirmReviewer";
import ConfirmOrganizationReviewer from "pages/ConfirmOrganizationReviewer";
import Dashboard from "pages/Dashboard";
import FrontPage from "pages/FrontPage";
import NotFoundPage from "pages/NotFound";
import AboutUs from "pages/FrontPage/InnerPages/AboutUs";
import LegalDocuments from "pages/FrontPage/LegalDocuments";
import Contacts from "pages/FrontPage/ContactUs";
import Forbidden from "pages/Forbidden";
import { useSocket } from "context/SocketContext";
import { useToast } from "hooks/useToast";
import CookieCleanup from "components/lib/Global/CookieCleanup";
import ProtectedRoute from "components/Auth/ProtectedRoute";
import CompanyFormationPage from "pages/CompanyFormation";
import PaymentSuccessPage from "pages/Payments/Success";
import Corporates from "components/Applications/Corporates";
import ContactPersonCompaniesListPage from "pages/ContactPersonCompaniesList";
import DraftApplications from "components/Applications/Draft";
import Signature from "components/Applications/Signature";
import PaymentFailedPage from "pages/PaymentFailedPage";
import ApplicationsPending from "components/Applications/Pending";
import ProfilesPending from "components/Profiles";
import Profile from "pages/Profile";
import ProfileEdit from "pages/Profile/Edit";
import DashboardAgainstRole from "pages/Dashboard/DashboardAgainstRole";
import Transactions from "pages/Transactions";
import Corrected from "components/Applications/Corrected";
import Deferred from "components/Applications/Deferred";
import BRSSubmission from "components/Applications/BRSSubmission";
import AllApplications from "components/Applications/AllApplications";
import CompanyDashboardPage from "pages/CompanyDashboard";
import ApplicationReview from "pages/ApplicationReview";
import CompaniesList from "pages/CompaniesList";
import PublicLimitedCompanyPage from "pages/PublicLimitedCompany";
import CheckoutSuccess from "pages/CheckoutSuccess";
import PrivateLimitedCompanyPage from "pages/PrivateLimitedCompany";
import SuccessfulSubmission from "pages/SuccessfulSubmission";
import OrganizationsPage from "pages/Organizations";
import CorporateOnboarding from "components/CorporateOnboarding";
import CorporateDashboardPage from "pages/CorporateDashboard";
import VerifyAccountHandler from "components/Auth/verifyAccountHandler";
import { getCurrentUser } from "state/slices/auth";
import ToastContainer from "components/lib/Shared/ToastContainer";
import ConfirmAccount from "pages/Auth/ConfirmAccount";
import InActivityTracker from "components/lib/Global/InActivityTracker";
import Cookies from "js-cookie";
import AcceptCoApplicantInvitePage from "pages/AcceptCoApplicantInvite";
import MangeYourCompanyComponent from "pages/CompanyDashboard/MangeYourCompany";
import SignatureCorrectionPage from "pages/SignatureCorrectionPage";
import EmbeddedClientSigningPage from "pages/EmbeddedClientSigningPage";
import TransactionEdit from "components/CompanyDashboard/TransactionEditReviewSignature/TransactionEdit";
import RecentChangesAndApprovals from "components/CompanyDashboard/MaintainYourCompany/Changes/RecentChangesAndApprovals";
import ChangesPage from "pages/Changes";
import TransactionReview from "components/CompanyDashboard/TransactionEditReviewSignature/TransactionReview";
import TransactionChangesSignature from "components/CompanyDashboard/TransactionEditReviewSignature/TransactionChangesSignature";
import TransactionApprovalsSignature from "components/CompanyDashboard/TransactionEditReviewSignature/TransactionApprovalsSignature";
import TransactionBRSFormsSignature from "components/CompanyDashboard/TransactionEditReviewSignature/TransactionBRSFormsSignature";
import TransactionView from "components/CompanyDashboard/TransactionEditReviewSignature/TransactionView";
import ManualTransactionReview from "pages/ManualTransactionReview";
import ManualTransactionEdit from "pages/ManualTransactionEdit";
import ManualTransactionView from "pages/ManualTransactionView";
import TransferYourBusiness from "components/TransferYourBusiness";
import TransferYourBusinessReview from "pages/TransferYourBusinessReview";
import ApplicationReviewForTransfer from "pages/ApplicationReviewForTransfer";
import sessionManager from "./services/sessionManager";
import { Constants } from "config/constants";
import PaystackCallBackUrl from "pages/PaystackCallBackUrl";
import OrgAdmins from "components/Applications/OrgAdmins";
import OrgComplianceDashboardPage from "pages/Compliance/OrgComplianceDashboard";
import SuperComplianceDashboardPage from "pages/Compliance/SuperComplianceDashboard";
import ComplianceTasksPage from "pages/Compliance/ComplianceTasks";
import ComplianceProtectedRoute from "components/Compliance/ComplianceProtectedRoute";
import ProfileUpgrade from "pages/Profile/Upgrade";

const getRedirectUri = () => {
  return window.location.origin;
};

const App = () => {
  const { isAuthenticated, isLoading, getAccessTokenSilently, logout } =
    useAuth0();

  const [authLoading, setAuthLoading] = useState(true);
  const [shouldForceLogout, setShouldForceLogout] = useState(false);
  const userEmail = useSelector((state) => state.userSlice?.email);
  const userRole = useSelector((state) => state.userSlice?.role);
  const dispatch = useDispatch();
  const socket = useSocket();
  const { toast } = useToast();
  const history = useHistory();

  // Initialize session manager and check for logout requirement
  useEffect(() => {
    const tabId = sessionManager.init();

    if (tabId === null) {
      // Should logout - last tab was closed
      setShouldForceLogout(true);
    }

    // Listen for logout events from other tabs
    const handleSessionLogout = () => {
      handleAppLogout();
    };

    window.addEventListener("session-logout", handleSessionLogout);

    return () => {
      window.removeEventListener("session-logout", handleSessionLogout);
      sessionManager.cleanup();
    };
  }, []);

  // Handle forced logout scenario
  useEffect(() => {
    if (shouldForceLogout && !isLoading) {
      handleAppLogout();
    }
  }, [shouldForceLogout, isLoading]);

  // Handle authentication state changes
  useEffect(() => {
    const handleAuthState = async () => {
      if (!isLoading) {
        try {
          if (isAuthenticated) {
            // Get access token
            const token = await getAccessTokenSilently();

            // Store token in BOTH locations for compatibility
            localStorage.setItem("access_token", token);
            localStorage.setItem("token", token); // Add this line - some APIs might look for this

            // Mark as authenticated in session manager
            sessionManager.setAuthenticated(true);

            // Get user data
            dispatch(getCurrentUser());
          } else {
            sessionManager.setAuthenticated(false);
          }
        } catch (error) {
          console.error("Auth initialization error:", error);

          // Handle specific Auth0 errors
          if (error.error === "login_required") {
            sessionManager.setAuthenticated(false);
          } else if (error.error === "consent_required") {
          } else if (error.error === "interaction_required") {
          }
        } finally {
          setAuthLoading(false);
        }
      }
    };

    handleAuthState();
  }, [isAuthenticated, isLoading, getAccessTokenSilently, dispatch]);

  // Socket listeners
  useEffect(() => {
    socket.on("redirect", (data) => history.push(data.redirectUrl));
    socket.on("toast", (data) => toast(data.type, data.message));

    socket.on("organization:removed", async (payload) => {
      // payload example: { corporateId, emails, matchedCount, modifiedCount, removedAt, removedBy }
      // Example: update UI / local state
      // removeOrgFromState(payload.corporateId);
      if (payload.emails.includes(userEmail)) {
        await dispatch(getCurrentUser());
        toast(
          "info",
          "Your organization has been removed by the system administrator. You will be logged out."
        );
        if (userRole !== Constants.ROLES.SYSTEM_ADMIN) {
          history.push("/");
        }
      }
    });

    return () => {
      socket.off("organization:removed");
      socket.off("redirect");
      socket.off("toast");
    };
  }, [socket, history, toast]);

  // Loading timeout
  useEffect(() => {
    const timer = setTimeout(() => {
      if (authLoading) {
        console.warn("Auth loading timeout → forcing render");
        setAuthLoading(false);
      }
    }, 10000);
    return () => clearTimeout(timer);
  }, [authLoading]);

  // Handle logout
  const handleAppLogout = useCallback(async () => {
    try {
      // Clear app-specific data
      const appDataKeys = [
        "access_token",
        "user",
        "expireAt",
        "InactivityLimitReached",
      ];

      appDataKeys.forEach((key) => localStorage.removeItem(key));

      Cookies.remove("role");

      sessionManager.setAuthenticated(false);
      sessionManager.broadcastLogout();

      // Call Auth0 logout
      logout({
        logoutParams: {
          returnTo: window.location.origin,
          federated: false,
        },
      });
    } catch (error) {
      console.error("Logout error:", error);
      // Fallback logout
      logout({ logoutParams: { returnTo: window.location.origin } });
    }
  }, [logout]);

  // Show loading
  if (authLoading || isLoading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          fontSize: "1.2rem",
          color: "#333",
        }}
      >
        Loading application...
      </div>
    );
  }

  return (
    <div className="relative min-h-screen bg-gray-50 text-sflPrimary font-main">
      <Switch>
        <Route
          path="/"
          exact
          render={() => (isAuthenticated ? <Dashboard /> : <FrontPage />)}
        />
        <Route path="/esign" component={EmbeddedClientSigningPage} />
        <Route path="/about" component={AboutUs} />
        <Route path="/legal" component={LegalDocuments} />
        <Route path="/contacts" component={Contacts} />
        <Route path="/login" component={SignInPage} />
        <Route path="/register" component={SignUpPage} />
        <Route path="/verify-account" component={VerifyAccountHandler} />
        <ProtectedRoute path="/profile/edit" component={ProfileEdit} />
        <ProtectedRoute path="/profile/upgrade" component={ProfileUpgrade} />
        <ProtectedRoute path="/profile" component={Profile} />
        <ProtectedRoute path="/account-settings" component={Profile} />
        <ProtectedRoute path="/billing" component={Profile} />
        <ProtectedRoute path="/dashboard" component={DashboardAgainstRole} />
        <ProtectedRoute path="/transactions" component={Transactions} />
        <ProtectedRoute
          path="/signature/:trxId"
          component={SignatureCorrectionPage}
        />
        <Route path="/confirm-account/:token" component={ConfirmAccount} />
        <Route path="/confirm-reviewer/:token" component={ConfirmReviewer} />
        <Route
          path="/organization-review/:token"
          component={ConfirmOrganizationReviewer}
        />
        <Route
          path="/accept-co-applicant-invite/:token"
          component={AcceptCoApplicantInvitePage}
        />
        <ProtectedRoute path="/payment-failed" component={PaymentFailedPage} />
        <ProtectedRoute
          path="/applications/pending"
          component={ApplicationsPending}
        />
        <ProtectedRoute
          path="/applications/draft"
          component={DraftApplications}
        />
        <ProtectedRoute path="/profiles" component={ProfilesPending} />
        <ProtectedRoute path="/applications/corrected" component={Corrected} />
        <ProtectedRoute path="/applications/deferred" component={Deferred} />
        <ProtectedRoute path="/applications/signature" component={Signature} />
        <ProtectedRoute
          path="/applications/brs-submission"
          component={BRSSubmission}
        />
        <ProtectedRoute
          path="/applications/all-applications"
          component={AllApplications}
        />
        <ProtectedRoute
          path="/companies/:companyId/dashboard/transactions/:trxId/recent-changes"
          component={RecentChangesAndApprovals}
        />
        <ProtectedRoute
          path="/companies/:companyId/dashboard/transactions/:trxId/edit"
          component={TransactionEdit}
        />
        <ProtectedRoute
          path="/companies/:companyId/dashboard/transactions/:trxId/review"
          component={TransactionReview}
        />
        <ProtectedRoute
          path="/companies/:companyId/dashboard/transactions/:trxId/view"
          component={TransactionView}
        />
        <ProtectedRoute
          path="/companies/:companyId/dashboard/transactions/:trxId/manual-edit"
          component={ManualTransactionEdit}
        />
        <ProtectedRoute
          path="/companies/:companyId/dashboard/transactions/:trxId/manual-review"
          component={ManualTransactionReview}
        />
        <ProtectedRoute
          path="/companies/:companyId/dashboard/transactions/:trxId/manual-view"
          component={ManualTransactionView}
        />
        <ProtectedRoute
          path="/companies/:companyId/dashboard/transactions/:trxId/brs-forms-signature"
          component={TransactionBRSFormsSignature}
        />
        <ProtectedRoute
          path="/companies/:companyId/dashboard/transactions/:trxId/changes-signature"
          component={TransactionChangesSignature}
        />
        <ProtectedRoute
          path="/companies/:companyId/dashboard/transactions/:trxId/approvals-signature"
          component={TransactionApprovalsSignature}
        />
        <ProtectedRoute
          path="/companies/:companyId/dashboard/transactions/:trxId"
          component={TransactionReview}
        />
        <ProtectedRoute
          path="/companies/:companyId/dashboard/manage-your-company/changes"
          component={ChangesPage}
        />
        <ProtectedRoute
          path="/companies/:companyId/dashboard/manage-your-company"
          component={MangeYourCompanyComponent}
        />
        <ProtectedRoute
          path="/companies/:companyId/dashboard"
          component={CompanyDashboardPage}
        />
        <ProtectedRoute
          path="/transferYourBusiness/:companyId/review"
          component={TransferYourBusinessReview}
        />
        <ProtectedRoute
          path="/transferYourBusiness/:companyId/view"
          component={TransferYourBusinessReview}
        />
        <ProtectedRoute
          path="/companies/:companyId/review"
          component={ApplicationReview}
        />
        <ProtectedRoute
          path="/companies/:companyId/view"
          component={ApplicationReview}
        />
        <ProtectedRoute
          path="/companies/:companyId/transfer-review"
          component={ApplicationReviewForTransfer}
        />
        <ProtectedRoute
          path="/companies/:companyId/transfer-view"
          component={ApplicationReviewForTransfer}
        />
        <ProtectedRoute
          path="/companies/:companyId"
          component={CompanyFormationPage}
        />
        <ProtectedRoute exact path="/companies" component={CompaniesList} />
        <ProtectedRoute
          path="/corporate-directors-shareholders"
          component={ContactPersonCompaniesListPage}
        />
        <ProtectedRoute
          path="/public-limited-company"
          component={PublicLimitedCompanyPage}
        />
        <ProtectedRoute path="/checkout-success" component={CheckoutSuccess} />
        <ProtectedRoute
          path="/applicant-transfer-business"
          component={TransferYourBusiness}
        />
        <ProtectedRoute
          path="/private-limited-company"
          component={PrivateLimitedCompanyPage}
        />
        <ProtectedRoute
          path="/successful-submission"
          component={SuccessfulSubmission}
        />
        <ProtectedRoute path="/organizations" component={OrganizationsPage} />
        <ProtectedRoute
          exact
          path="/corporates/:corporateId"
          component={CorporateOnboarding}
        />
        <ProtectedRoute
          path="/corporates/:corporateId/view"
          component={CorporateOnboarding}
        />
        <ProtectedRoute
          path="/corporates/:corporateId/dashboard"
          component={CorporateDashboardPage}
        />
        <ComplianceProtectedRoute
          path="/compliance/org-dashboard"
          component={OrgComplianceDashboardPage}
        />
        <ComplianceProtectedRoute
          path="/compliance/super-dashboard"
          component={SuperComplianceDashboardPage}
        />
        <ComplianceProtectedRoute
          path="/compliance/tasks"
          component={ComplianceTasksPage}
        />
        <ProtectedRoute path="/corporates" component={Corporates} />
        <ProtectedRoute path="/org-admins" component={OrgAdmins} />
        <ProtectedRoute
          path="/payments/success"
          component={PaymentSuccessPage}
        />
        <Route path="/access-denied" component={Forbidden} />

        <Route path="/paystack/callback" component={PaystackCallBackUrl} />
        <Route path="*" component={NotFoundPage} />
      </Switch>
      <ToastContainer />
      <CookieCleanup />
      <InActivityTracker onLogout={handleAppLogout} />
    </div>
  );
};

export default App;
